import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { HotToastService } from '@ngxpert/hot-toast';
import { Notification as NovuNotification } from '@novu/js';
import { Subscription } from 'rxjs';
import { InviteActionService } from 'src/services/invite-action.service';
import { NotificationItemComponent } from '../notification-item/notification-item.component';
import { NotificationService } from '../notification.service';

// Enhanced notification interface that includes our custom payload properties
interface EnhancedNotification extends NovuNotification {
  payload?: {
    route?: string;
    routePath?: string;
    routeData?: Record<string, any>;
    [key: string]: any;
  };
}

@Component({
  selector: 'app-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
  standalone: true,
  imports: [CommonModule, NotificationItemComponent]
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @ViewChild('notificationDropdown') notificationDropdown!: ElementRef;
  
  notifications: EnhancedNotification[] = [];
  loading = false;
  error = false;
  unreadCount = 0;
  
  private subscriptions: Subscription[] = [];
  
  loadingPrimaryId: string | null = null;
  loadingSecondaryId: string | null = null;
  
  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private inviteActionService: InviteActionService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.subscribeToNotifications();
  }
  
  // Subscribe to notification data
  private subscribeToNotifications(): void {
    // Subscribe to unread count for badge and header display
    this.subscriptions.push(
      this.notificationService.unreadCount$.subscribe(count => {
        this.unreadCount = count;
      })
    );
    
    // Subscribe to notifications data
    this.subscriptions.push(
      this.notificationService.notifications$.subscribe(notifications => {
        this.notifications = notifications as EnhancedNotification[];
        
        // Update loading state when notifications are received
        if (this.loading && notifications) {
          this.loading = false;
        }
      })
    );
  }
  
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
  
  // When the component becomes visible, refresh notifications
  @Input() set visible(isVisible: boolean) {
    if (isVisible) {
      this.refreshNotifications();
    }
  }
  
  // Manually refresh notifications
  async refreshNotifications(): Promise<void> {
    this.loading = true;
    this.error = false;
    
    try {
      await this.notificationService.fetchNotificationsList();
    } catch (error) {
      console.error('Failed to load notifications', error);
      this.error = true;
      this.loading = false;
    }
  }
  
  // Mark all notifications as read
  async markAllAsRead(): Promise<void> {
    try {
      await this.notificationService.notifications?.readAll();
      //await this.notificationService.fetchNotificationsList();
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
    }
  }

  // Toggle read status of a notification
  async toggleRead(notification: EnhancedNotification): Promise<void> {
    try {
      if (notification.isRead) {
        await notification.unread();
      } else {
        await notification.read();
      }
      //await this.notificationService.fetchNotificationsList();
    } catch (error) {
      console.error('Failed to toggle read status', error);
    }
  }

  // Archive a notification
  async archiveNotification(notification: EnhancedNotification): Promise<void> {
    try {
      this.notifications = this.notifications.filter(n => n.id !== notification.id);
      await notification.archive();
      //await this.notificationService.fetchNotificationsList();
    } catch (error) {
      console.error('Failed to archive notification', error);
    }
  }

  // Check if a notification has route data
  hasRouteData(notification: EnhancedNotification): boolean {
    if (!notification || !notification.payload) return false;
    
    return !!(
      notification.payload.route || 
      notification.payload.routePath || 
      notification.payload.routeData
    );
  }
  
  // Handle notification click for routing
  async handleNotificationClick(notification: EnhancedNotification): Promise<void> {
    if (!notification || !notification.payload) return;
    
    try {
      // Mark notification as read when clicked
      if (!notification.isRead) {
        await notification.read();
      }
      
      // Extract route information from payload
      const payload = notification.payload;
      
      // Handle different routing formats
      if (payload.route) {
        this.router.navigate([payload.route]);
      } 
      else if (payload.routePath && payload.routeData) {
        this.router.navigate([payload.routePath], { 
          queryParams: payload.routeData 
        });
      }
      else if (payload.routePath) {
        this.router.navigate([payload.routePath]);
      }
      else if (notification.redirect?.url) {
        // Support for the new format using redirect property
        const url = notification.redirect.url;
        if (url.startsWith('/')) {
          this.router.navigateByUrl(url);
        } else {
          window.open(url, notification.redirect.target || '_self');
        }
      }
      
      // Refresh notifications to update status
      //await this.notificationService.fetchNotificationsList();
    } catch (error) {
      console.error('Failed to handle notification click', error);
    }
  }

  // Handle notification action (primary or secondary)
  async handleAction(notification: EnhancedNotification, actionType: 'primary' | 'secondary'): Promise<void> {
    try {
      let actionUrl = '';
      if (actionType === 'primary' && notification.primaryAction) {
        this.loadingPrimaryId = notification.id;
        await notification.completePrimary();
        actionUrl = notification.primaryAction.redirect?.url || '';
      } else if (actionType === 'secondary' && notification.secondaryAction) {
        this.loadingSecondaryId = notification.id;
        await notification.completeSecondary();
        actionUrl = notification.secondaryAction.redirect?.url || '';
      }
      // Check for invite URL pattern: /invite/:type/:id?action=accept|decline
      const inviteRegex = /^\/invite\/(group|relation)\/([^/?]+)\?action=(accept|decline)$/;
      const match = actionUrl.match(inviteRegex);
      if (match) {
        const [, type, id, action] = match;
        this.inviteActionService.handleInviteAction(type, id, action).subscribe({
          next: async () => {
            this.toast.success(`Invite ${action}ed successfully!`);
            
            // Backend already deletes the Novu notification, so we just remove from UI
            // No need to call notification.archive() as backend handles it via deleteInAppNotification
            this.notifications = this.notifications.filter(n => n.id !== notification.id);
            this.loadingPrimaryId = null;
            this.loadingSecondaryId = null;
          },
          error: (err) => {
            this.toast.error(err.message || 'Failed to process invite action.');
            this.loadingPrimaryId = null;
            this.loadingSecondaryId = null;
          }
        });
        return;
      }
      // Fallback to default navigation logic
      if (actionUrl.startsWith('/')) {
        this.router.navigateByUrl(actionUrl);
      } else if (actionUrl) {
        window.open(
          actionUrl,
          (actionType === 'primary' ? notification.primaryAction?.redirect?.target : notification.secondaryAction?.redirect?.target) || '_self'
        );
      }
      this.loadingPrimaryId = null;
      this.loadingSecondaryId = null;
    } catch (error) {
      this.loadingPrimaryId = null;
      this.loadingSecondaryId = null;
      console.error('Failed to handle notification action', error);
    }
  }

  // Retry loading notifications when there was an error
  async retryLoad(): Promise<void> {
    await this.refreshNotifications();
  }

  get visibleNotifications() {
    return this.notifications.filter(item => !item.isArchived)
  }
} 